import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { User, Building, MessageCircle, Share2, CheckCircle, Clock, X } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface Profile {
  organization_id: string;
  username: string;
  name: string;
  organization_type: 'carrier' | 'broker' | 'customer' | 'logistics_provider';
}

interface Connection {
  id: string;
  status: string;
  inviter_id: string;
  invitee_id: string;
}

export const PublicProfile = () => {
  const { username } = useParams();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (username) {
      loadProfile();
      if (user) {
        loadConnection();
      }
    }
  }, [username, user]);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_public_profile', { username_input: username });

      if (error) throw error;

      if (data && data.length > 0) {
        setProfile(data[0]);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Error",
        description: "Failed to load profile",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadConnection = async () => {
    if (!profile || !user) return;

    try {
      // For now, disable connections since we're working with organizations
      // This would need to be implemented differently with organization-based connections
      setConnection(null);
    } catch (error) {
      console.error('Error loading connection:', error);
    }
  };

  const handleConnect = async () => {
    // Disabled for organization profiles - would need different implementation
    return;
  };

  const handleRespondToConnection = async (response: 'active' | 'inactive') => {
    if (!connection) return;

    try {
      const { error } = await supabase
        .from('connections')
        .update({ status: response })
        .eq('id', connection.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Connection ${response}`
      });

      loadConnection();
    } catch (error) {
      console.error('Error responding to connection:', error);
      toast({
        title: "Error",
        description: "Failed to respond to connection",
        variant: "destructive"
      });
    }
  };

  const shareProfile = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.name || profile?.username}'s Profile`,
          url: url
        });
      } catch (error) {
        // User cancelled share
      }
    } else {
      // Fallback to copy
      navigator.clipboard.writeText(url);
      toast({
        title: "Copied!",
        description: "Profile link copied to clipboard"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="animate-pulse">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto py-8">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Profile Not Found</h1>
          <p className="text-muted-foreground">
            The username @{username} doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const isOwnProfile = false; // Disabled for organization profiles
  const canConnect = !isOwnProfile && !connection;
  const isPendingFromMe = connection?.inviter_id === user?.id && connection?.status === 'pending';
  const isPendingToMe = connection?.invitee_id === user?.id && connection?.status === 'pending';
  const isConnected = connection?.status === 'accepted';

  return (
    <div className="container mx-auto py-8">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1" />
              <Avatar className="w-24 h-24">
                <AvatarFallback className="text-2xl">
                  {profile.name ? profile.name.charAt(0).toUpperCase() : profile.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 flex justify-end">
                <Button variant="outline" size="sm" onClick={shareProfile}>
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <CardTitle className="text-2xl">{profile.name || profile.username}</CardTitle>
            <p className="text-muted-foreground text-lg">@{profile.username}</p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <Badge variant="secondary" className="flex items-center gap-2">
                <User className="h-3 w-3" />
                {profile.organization_type.charAt(0).toUpperCase() + profile.organization_type.slice(1).replace('_', ' ')}
              </Badge>
            </div>

            <Separator className="mb-6" />

            {!isOwnProfile && (
              <div className="space-y-3">
                {canConnect && (
                  <Button 
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="w-full bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {isConnecting ? 'Sending...' : 'Connect'}
                  </Button>
                )}

                {isPendingFromMe && (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Connection request sent</span>
                  </div>
                )}

                {isPendingToMe && (
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleRespondToConnection('active')}
                      className="flex-1 bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accept
                    </Button>
                    <Button 
                      onClick={() => handleRespondToConnection('inactive')}
                      variant="outline"
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                )}

                {isConnected && (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>Connected</span>
                  </div>
                )}
              </div>
            )}

            {isOwnProfile && (
              <div className="text-center text-muted-foreground">
                <p>This is your public profile</p>
                <p className="text-sm mt-2">Share your username @{profile.username} to let others find you</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};